import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  Pill,
  PrimaryButton,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { useMedicine } from '../../hooks/useMedicines'
import SideEffectDialog from './components/SideEffectDialog'
import { parseUtcDate } from '../../utils/dates'

const HistoryRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 10px 0;
  & + & {
    border-top: 1px dashed ${(p) => p.theme?.color?.border || '#E6E0D6'};
  }
`

const StatusPill = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$status === 'TAKEN'
      ? p.theme?.color?.accentSoft || '#E1ECE7'
      : p.$status === 'SKIPPED' || p.$status === 'MISSED'
      ? p.theme?.color?.urgentSoft || '#FBEAE9'
      : p.theme?.color?.bgSubtle || '#F4F0E9'};
  color: ${(p) =>
    p.$status === 'TAKEN'
      ? p.theme?.color?.accent || '#6B9080'
      : p.$status === 'SKIPPED' || p.$status === 'MISSED'
      ? p.theme?.color?.urgent || '#C24D49'
      : p.theme?.color?.inkMuted || '#6B7280'};
`

const SeverityPill = styled(StatusPill)`
  background: ${(p) =>
    p.$severity === 'SEVERE'
      ? p.theme?.color?.urgentSoft || '#FBEAE9'
      : p.$severity === 'MODERATE'
      ? p.theme?.color?.bgSubtle || '#F4F0E9'
      : p.theme?.color?.accentSoft || '#E1ECE7'};
  color: ${(p) =>
    p.$severity === 'SEVERE'
      ? p.theme?.color?.urgent || '#C24D49'
      : p.theme?.color?.ink || '#1F2937'};
`

function fmtDateTime(iso) {
  if (!iso) return ''
  return parseUtcDate(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function summariseAdherence(history) {
  if (!history?.length) return null
  const total = history.length
  const taken = history.filter((h) => h.status === 'TAKEN' || h.status === 'LATE').length
  return Math.round((taken / total) * 100)
}

export default function MedicineDetailPage() {
  const navigate = useNavigate()
  const { medicineId } = useParams()
  const { medicine, history, sideEffects, loading, recordSideEffect } = useMedicine(medicineId)
  const [seOpen, setSeOpen] = useState(false)

  if (loading && !medicine) {
    return (
      <ContentMaxWidth>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <LoadingSpinner />
        </div>
      </ContentMaxWidth>
    )
  }

  if (!medicine) {
    return (
      <ContentMaxWidth>
        <Stack $gap="20px">
          <PageHeading>Medicine not found</PageHeading>
          <GhostButton onClick={() => navigate('/patient-home/care/medicines')}>
            Back to medicines
          </GhostButton>
        </Stack>
      </ContentMaxWidth>
    )
  }

  const adherence = summariseAdherence(history)

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/care/medicines">Back to medicines</BackLink>

        <Row $between $wrap $gap="12px">
          <div>
            <Pill>{medicine.isActive ? 'Active' : 'Archived'}</Pill>
            <PageHeading style={{ marginTop: 12 }}>
              {medicine.name}{' '}
              <span style={{ color: '#6B7280', fontWeight: 400 }}>· {medicine.dosage}</span>
            </PageHeading>
            <Muted>
              {medicine.frequencyType === 'DAILY'
                ? `Daily · ${(medicine.timesOfDay || []).join(', ')}`
                : medicine.frequencyType === 'WEEKLY'
                ? `Weekly · ${(medicine.daysOfWeek || []).join(', ')}`
                : 'As needed'}{' '}
              · prescribed by {medicine.prescribedBy || 'self'}
            </Muted>
          </div>
          <Row $gap="10px">
            <GhostButton onClick={() => setSeOpen(true)}>Log side effect</GhostButton>
          </Row>
        </Row>

        {medicine.instructions && (
          <Card>
            <CardTitle style={{ marginBottom: 6 }}>Instructions</CardTitle>
            <Muted>{medicine.instructions}</Muted>
          </Card>
        )}

        <Card>
          <Row $between>
            <CardTitle style={{ margin: 0 }}>Last 14 days</CardTitle>
            {adherence != null && <Pill>{adherence}% adherence</Pill>}
          </Row>
          {!history?.length ? (
            <Muted style={{ marginTop: 12 }}>
              No intake history yet — once you mark doses taken or skipped on Today, they show up here.
            </Muted>
          ) : (
            <div style={{ marginTop: 12 }}>
              {history.map((h) => (
                <HistoryRow key={h.intakeId}>
                  <div style={{ width: 130, color: '#6B7280', fontSize: 13 }}>
                    {fmtDateTime(h.scheduledFor || h.actualTakenAt)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <StatusPill $status={h.status}>{h.status}</StatusPill>
                    {h.note && (
                      <div style={{ marginTop: 4, color: '#4B5563', fontSize: 13 }}>
                        {h.note}
                      </div>
                    )}
                  </div>
                </HistoryRow>
              ))}
            </div>
          )}
        </Card>

        {sideEffects?.length > 0 && (
          <Card>
            <CardTitle style={{ marginBottom: 12 }}>Side effects logged</CardTitle>
            {sideEffects.map((se) => (
              <HistoryRow key={se.id}>
                <div style={{ width: 130, color: '#6B7280', fontSize: 13 }}>
                  {fmtDateTime(se.recordedAt)}
                </div>
                <div style={{ flex: 1 }}>
                  <SeverityPill $severity={se.severity}>{se.severity}</SeverityPill>
                  <div style={{ marginTop: 4, color: '#1F2937', fontSize: 14 }}>{se.description}</div>
                </div>
              </HistoryRow>
            ))}
          </Card>
        )}
      </Stack>

      <SideEffectDialog
        open={seOpen}
        medicineName={medicine.name}
        onClose={() => setSeOpen(false)}
        onSubmit={async (body) => {
          await recordSideEffect(body)
        }}
      />
    </ContentMaxWidth>
  )
}
